import { CSpinner } from "@coreui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useReducer, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import BoxWrapper from "../../components/ui/BoxWrapper";
import DeleteIcon from "../../components/icons/DeleteIcon";
import Pagination from "../../components/ui/Pagination";
import RenderTable from "../../components/ui/RenderTable";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../context/bulkDeleteReducer";
import { pluralize } from "../../helpers/general";
import { useTranslationsQuery } from "../../hooks/Translations/useTranslationQuery";
import { general } from "../../locales/general";
import AdminRoute from "../auth/RestrictedRoute";
import CategoryTranslationsTable from "./Categories/CategoriesTranslationsTable";
import EventTranslationsTable from "./Events/EventsTranslationsTable";
import PackTranslationsTable from "./Packs/PacksTranslationsTable";
import PlaceTranslationsTable from "./Places/PlacesTranslationsTable";
import RewardsTranslationsTable from "./Rewards/RewardsTranslationsTable";
import ServiceCategoryTranslationsTable from "./ServiceCategories/ServiceCategoriesTranslationsTable";
import ServiceTranslationsTable from "./Services/ServicesTranslationsTable";
import ServiceSubCategoryTranslationsTable from "./serviceSubCategory/AllSubCategories";
import SpecialTranslationsTable from "./Specials/SpecialsTranslationsTable";

const PAGE_SIZE = 10;

type Dispatch = React.Dispatch<BulkDeleteActions>;

interface TabConfig {
  /** Value sent to useTranslationsQuery (also the URL hash without "#") */
  key: string;
  label: string;
  /** Route of the "create translation" page */
  createPath: string;
  /** Renders the table for this tab */
  render: (items: any[], dispatch: Dispatch) => React.ReactNode;
}

/**
 * One entry per tab. To add a new translation type, add an entry here —
 * nothing else in the page needs to change.
 */
const TABS: TabConfig[] = [
  {
    key: "place",
    label: "Établissements",
    createPath: "/place-translations/new",
    render: (items, dispatch) => (
      <PlaceTranslationsTable
        placeTranslations={items as PlaceTranslation[]}
        placeTranslationsToBeDeleted={dispatch}
      />
    ),
  },
  {
    key: "event",
    label: "Évènements",
    createPath: "/event-translations/new",
    render: (items, dispatch) => (
      <EventTranslationsTable
        eventTranslations={items as EventTranslation[]}
        eventTranslationsToBeDeleted={dispatch}
      />
    ),
  },
  {
    key: "special",
    label: "Offres",
    createPath: "/special-translations/new",
    render: (items, dispatch) => (
      <SpecialTranslationsTable
        specialTranslations={items as SpecialTranslation[]}
        specialTranslationsToBeDeleted={dispatch}
      />
    ),
  },
  {
    key: "category",
    label: "Catégories",
    createPath: "/category-translations/new",
    render: (items, dispatch) => (
      <CategoryTranslationsTable
        categoryTranslations={items as CategoryTranslation[]}
        categoryTranslationsToBeDeleted={dispatch}
      />
    ),
  },
  {
    key: "service",
    label: "Services",
    createPath: "/service-translations/new",
    render: (items, dispatch) => (
      <ServiceTranslationsTable
        serviceTranslations={items as ServiceTranslation[]}
        serviceTranslationsToBeDeleted={dispatch}
      />
    ),
  },
  {
    key: "service-category",
    label: "Les catégories des services",
    createPath: "/service-category-translations/new",
    render: (items, dispatch) => (
      <ServiceCategoryTranslationsTable
        serviceCategoryTranslations={items as ServiceCategoryTranslation[]}
        serviceCategoryTranslationsToBeDeleted={dispatch}
      />
    ),
  },
  {
    key: "service-sub-category",
    label: "Sous-catégorie de service",
    createPath: "/service-sub-category-translations/new",
    render: (items, dispatch) => (
      <ServiceSubCategoryTranslationsTable
        serviceCategoryTranslations={items as ServiceSubCategoryTranslation[]}
        serviceCategoryTranslationsToBeDeleted={dispatch}
      />
    ),
  },
  {
    key: "pack",
    label: "Packs",
    createPath: "/pack-translations/new",
    render: (items, dispatch) => (
      <PackTranslationsTable
        packTranslations={items as PackTranslation[]}
        packTranslationsToBeDeleted={dispatch}
      />
    ),
  },
  {
    key: "rewards",
    label: "Bonus",
    createPath: "/reward-translations/new",
    render: (items, dispatch) => (
      <RewardsTranslationsTable
        rewardTranslations={items as RewardTranslation[]}
        rewardTranslationsToBeDeleted={dispatch}
      />
    ),
  },
];

function TranslationSvg() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="white"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.7044 3.51898C10.034 3.51898 9.46373 3.9848 9.30365 4.61265H14.6863C14.5263 3.9848 13.956 3.51898 13.2856 3.51898H10.7044ZM16.2071 4.61264H18.1881C20.2891 4.61264 22 6.34428 22 8.47085C22 8.47085 21.94 9.3711 21.92 10.6248C21.918 10.724 21.8699 10.8212 21.7909 10.88C21.3097 11.2354 20.8694 11.5291 20.8294 11.5493C19.1686 12.6632 17.2386 13.447 15.1826 13.8369C15.0485 13.8632 14.9165 13.7934 14.8484 13.6739C14.2721 12.6754 13.1956 12.0253 11.995 12.0253C10.8024 12.0253 9.71586 12.6683 9.12256 13.6678C9.05353 13.7853 8.92346 13.8531 8.7904 13.8278C6.75138 13.4369 4.82141 12.6541 3.17059 11.5594L2.21011 10.8911C2.13007 10.8405 2.08004 10.7493 2.08004 10.6481C2.05003 10.1316 2 8.47085 2 8.47085C2 6.34428 3.71086 4.61264 5.81191 4.61264H7.78289C7.97299 3.1443 9.2036 2 10.7044 2H13.2856C14.7864 2 16.017 3.1443 16.2071 4.61264ZM21.6598 12.8152L21.6198 12.8355C19.5988 14.1924 17.1676 15.0937 14.6163 15.4684C14.2561 15.519 13.8959 15.2861 13.7959 14.9216C13.5758 14.0912 12.8654 13.5443 12.015 13.5443H12.005H11.985C11.1346 13.5443 10.4242 14.0912 10.2041 14.9216C10.1041 15.2861 9.74387 15.519 9.38369 15.4684C6.83242 15.0937 4.4012 14.1924 2.38019 12.8355C2.37019 12.8254 2.27014 12.7646 2.1901 12.8152C2.10005 12.8659 2.10005 12.9874 2.10005 12.9874L2.17009 18.1519C2.17009 20.2785 3.87094 22 5.97199 22H18.018C20.1191 22 21.8199 20.2785 21.8199 18.1519L21.9 12.9874C21.9 12.9874 21.9 12.8659 21.8099 12.8152C21.7599 12.7849 21.6999 12.795 21.6598 12.8152ZM12.7454 17.0583C12.7454 17.4836 12.4152 17.8177 11.995 17.8177C11.5848 17.8177 11.2446 17.4836 11.2446 17.0583V15.7519C11.2446 15.3367 11.5848 14.9924 11.995 14.9924C12.4152 14.9924 12.7454 15.3367 12.7454 15.7519V17.0583Z"
      />
    </svg>
  );
}

/* ─────────────── Tab bar ─────────────── */

function TabBar({ activeKey }: { activeKey: string }) {
  return (
    <nav
      aria-label="Types de traductions"
      className="d-flex gap-2 px-3 py-3 overflow-auto"
      style={{ borderBottom: "1px solid #eee" }}
    >
      {TABS.map((tab) => {
        const active = tab.key === activeKey;
        return (
          <Link
            key={tab.key}
            to={{ hash: `#${tab.key}` }}
            aria-current={active ? "page" : undefined}
            className="rounded-pill text-decoration-none"
            style={{
              padding: "7px 16px",
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: "nowrap",
              color: active ? "#fff" : "#555",
              backgroundColor: active ? "#f0813f" : "#f5f5f5",
              transition: "background-color .15s, color .15s",
            }}
          >
            {tab.label}
          </Link>
        );
      })}
    </nav>
  );
}

/* ─────────────── Page ─────────────── */

export default function Translations() {
  const [searchQuery, setSearchQuery] = useState("");
  const { hash } = useLocation();

  // The active tab comes straight from the URL hash (defaults to the first one)
  const activeTab = TABS.find((tab) => `#${tab.key}` === hash) ?? TABS[0];

  const {
    translations,
    totalPages,
    totalCount,
    fetchingTranslations,
    deleteTranslation,
    mutating,
    currentPage,
    searchHandler,
    setCurrentPage,
  } = useTranslationsQuery(activeTab.key);

  const [toBeDeleted, dispatch] = useReducer(
    bulkDeleteReducer,
    bulkDeleteState,
  );

  // When switching tab: reset search, page and any selection from the old tab
  useEffect(() => {
    setSearchQuery("");
    setCurrentPage(1);
    toBeDeleted.ids.forEach((id) =>
      dispatch({ type: DELETE_ACTIONS.REMOVE, id }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab.key]);

  const handleBulkDelete = () => {
    toBeDeleted.ids.forEach((id) => {
      if (!id) return;
      deleteTranslation(id);
      toBeDeleted.ids = [];
    });
  };

  return (
    <AdminRoute>
      <h5 className="fs-4 fw-bold mb-2">Traductions</h5>
      <BoxWrapper>
        <TabBar activeKey={activeTab.key} />

        {/* Toolbar (shared by every tab) */}
        <div className="d-flex justify-content-between align-items-baseline before-table">
          <div className="d-flex align-items-center gap-2 p-3 delete-add">
            <Link
              to={activeTab.createPath}
              className="outline-none border-0 btn btn-primary rounded-pill text-white d-flex align-items-center gap-2 justify-content-center shadow-secondary text-pre"
            >
              + Ajouter une traduction
            </Link>

            <button
              disabled={mutating || toBeDeleted?.ids.length === 0}
              onClick={handleBulkDelete}
              className="outline-none btn btn-outline-danger rounded-pill text-danger d-flex align-items-center gap-2 justify-content-center shadow-primary"
            >
              {!mutating ? (
                <>
                  <DeleteIcon fill={colors.primary} /> Supprimer
                </>
              ) : (
                <>
                  <CSpinner size="sm" /> {general.fr.wait}
                </>
              )}
            </button>
          </div>

          <div className="search-form">
            <div className="input-field">
              <MagnifyingGlassIcon color="gray" width={20} />
              <input
                type="text"
                className="w-50 border-0 outline-none rounded-sm p-1 text-gray fs-6"
                placeholder="Rechercher"
                value={searchQuery}
                onChange={(e) => {
                  searchHandler(e.target.value?.toLowerCase());
                  setSearchQuery(e.target.value);
                }}
              />
            </div>
          </div>

          <div
            className="d-flex align-items-center justify-content-center gap-3 bg-gradient-primary p-3"
            style={{ borderTopRightRadius: "8px", alignSelf: "flex-start" }}
          >
            <TranslationSvg />
            <p className="fs-5 fw-bold m-0 text-white text-pre">
              {totalCount} {pluralize("Traduction", totalCount)}
            </p>
          </div>
        </div>

        {/* Only the active tab's table is rendered */}
        <RenderTable
          loading={fetchingTranslations}
          render={() =>
            translations?.length > 0 ? (
              activeTab.render(translations, dispatch)
            ) : (
              <p className="text-center fs-5 my-2">{general.fr.noData}</p>
            )
          }
        />
      </BoxWrapper>

      <Pagination
        currentPage={currentPage}
        onPageChange={(page) => {
          setCurrentPage(page);
        }}
        siblingCount={0}
        totalCount={totalPages}
        pageSize={PAGE_SIZE}
      />
    </AdminRoute>
  );
}
